import { useState } from 'react'
import { isoWeekday, parts } from '@/logic/dates'
import { RECURRENCE_PRESETS, presetKeyOf } from '@/logic/recurrence'
import type { DateStr, Recurrence, RecurrenceFreq } from '@/types'
import { Button } from './Button'
import { Segmented } from './Segmented'
import { Sheet } from './Sheet'
import './RecurrenceSheet.css'

interface RecurrenceSheetProps {
  open: boolean
  dueDate: DateStr
  value: Recurrence | null
  onChange: (r: Recurrence | null) => void
  onClose: () => void
}

const WEEKDAYS = [
  { n: 1, label: 'M', name: 'Monday' },
  { n: 2, label: 'T', name: 'Tuesday' },
  { n: 3, label: 'W', name: 'Wednesday' },
  { n: 4, label: 'T', name: 'Thursday' },
  { n: 5, label: 'F', name: 'Friday' },
  { n: 6, label: 'S', name: 'Saturday' },
  { n: 7, label: 'S', name: 'Sunday' },
]

/** Repeat: the presets and Custom (every N days, weeks or months, with weekday choice). The kind control turns repeating off. */
export function RecurrenceSheet({ open, dueDate, value, onChange, onClose }: RecurrenceSheetProps) {
  const current = presetKeyOf(value)
  const [custom, setCustom] = useState(current === 'custom')
  const [freq, setFreq] = useState<RecurrenceFreq>(value?.freq ?? 'week')
  const [interval, setInterval] = useState(value?.interval ?? 2)
  const [weekdays, setWeekdays] = useState<number[]>(value?.weekdays ?? [isoWeekday(dueDate)])

  const pick = (r: Recurrence | null) => {
    onChange(r)
    onClose()
  }

  const applyCustom = () => {
    pick({
      freq,
      interval: Math.max(1, Math.min(99, Math.round(interval) || 1)),
      weekdays: freq === 'week' ? (weekdays.length ? [...weekdays].sort((a, b) => a - b) : [isoWeekday(dueDate)]) : null,
      monthDay: freq === 'month' ? parts(dueDate).d : null,
      basis: value?.basis ?? 'schedule',
      scheduledDate: dueDate,
      scheduledTime: value?.scheduledTime ?? null,
    })
  }

  return (
    <Sheet open={open} onClose={onClose} heading="Repeat" variant="dialog">
      <div className="confirm__choices">
        {RECURRENCE_PRESETS.map((p) => (
          <button
            type="button"
            key={p.key}
            className={`confirm__choice ${current === p.key ? 'rec__choice--on' : ''}`}
            onClick={() => pick({ ...p.make(dueDate), basis: value?.basis ?? 'schedule', scheduledTime: value?.scheduledTime ?? null })}
          >
            {p.label}
          </button>
        ))}
        <button type="button" className={`confirm__choice ${custom ? 'rec__choice--on' : ''}`} aria-expanded={custom} onClick={() => setCustom((v) => !v)}>
          Custom
        </button>
      </div>
      {custom && (
        <div className="rec__custom">
          <div className="rec__every">
            <span>Every</span>
            <label className="sr-only" htmlFor="rec-interval">
              Interval
            </label>
            <input
              id="rec-interval"
              type="number"
              className="rec__n"
              min={1}
              max={99}
              inputMode="numeric"
              value={interval}
              onChange={(e) => setInterval(Number(e.target.value))}
            />
            <Segmented<RecurrenceFreq>
              label="Unit"
              value={freq}
              options={[
                { value: 'day', label: 'days' },
                { value: 'week', label: 'weeks' },
                { value: 'month', label: 'months' },
              ]}
              onChange={setFreq}
            />
          </div>
          {freq === 'week' && (
            <div className="rec__days" role="group" aria-label="On these days">
              {WEEKDAYS.map((d) => {
                const on = weekdays.includes(d.n)
                return (
                  <button
                    type="button"
                    key={d.n}
                    className={`rec__day ${on ? 'rec__day--on' : ''}`}
                    aria-pressed={on}
                    aria-label={d.name}
                    onClick={() => setWeekdays((w) => (on ? w.filter((x) => x !== d.n) : [...w, d.n]))}
                  >
                    {d.label}
                  </button>
                )
              })}
            </div>
          )}
          <Button variant="primary" block onClick={applyCustom}>
            Use this rule
          </Button>
        </div>
      )}
    </Sheet>
  )
}
